import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { useCurrentUnit } from './useCurrentUnit.js'

// useTimeSaved — reads the "hours back since you joined" ledger.
//
// SOURCE OF TRUTH: public.savings_events rows where source='time_saved'.
// The figure = SUM(time_saved_minutes) over those rows, all-time. Every
// other source carries time_saved_minutes=NULL and is excluded. This is
// the SAME filter the edge fn / aggregate.ts enforces — kept in lockstep
// here via the TIME_SAVED_SOURCE constant so the card and the roll-up
// can't drift.
//
// SCOPE (mirrors useHuddleData's canonical per-unit pattern):
//   - Single-unit (currentRestaurantId null, not grouped): plain client
//     RLS query, byte-identical membership scoping, no restaurant filter.
//   - Grouped with a unit SELECTED (currentRestaurantId non-null): same
//     client query, scoped .eq('restaurant_id', currentRestaurantId) so a
//     multi-membership RLS merge can't bleed other units in.
//   - Grouped with NO unit selected (all_units roll-up: isGrouped &&
//     currentRestaurantId == null): the savings_events RLS policy is
//     MEMBER-ONLY, so a pure group owner reads ZERO child-unit rows
//     client-side. We call the get-time-saved-summary EDGE FN (service-
//     role, resolveGroupOwner-gated) for the group total instead — the
//     same asymmetry fix as useGroupMenuEngineering.
//
// Returns { totalMinutes, totalHours, asOf, eventCount, loading, error }.
// totalHours is the raw decimal hours (minutes / 60) — the card formats
// the headline via formatHoursSaved(totalMinutes); totalHours is provided
// for any caller that wants a numeric hours value.

// The single discriminator value, kept in lockstep with the edge fn's
// aggregate.ts TIME_SAVED_SOURCE. Surfaces must never inline the literal.
export const TIME_SAVED_SOURCE = 'time_saved'

export function useTimeSaved() {
  const { isGrouped, currentRestaurantId, currentGroupId } = useCurrentUnit()

  const [totalMinutes, setTotalMinutes] = useState(0)
  const [asOf, setAsOf]                 = useState(null)
  const [eventCount, setEventCount]     = useState(0)
  const [loading, setLoading]           = useState(true)
  const [error, setError]               = useState(null)

  // Roll-up path applies only when grouped AND no single unit is selected
  // (the all-units view). In every other case we read the ledger client-
  // side under RLS.
  const useGroupRollup = isGrouped && !currentRestaurantId

  // The secondary dollars line (vw_savings_summary.lifetime_total) is a
  // GROUP-WIDE sum with no restaurant_id. The hours headline is unit-scoped.
  // So it's only apples-to-apples with the hours when we are NOT looking at
  // a single unit inside a grouped account: i.e. the group roll-up view OR a
  // single-unit (ungrouped) account. When a grouped operator has drilled into
  // one unit, the group dollars would imply that unit's smaller hours are
  // worth the whole group's dollars — so the card hides the dollars line then.
  const dollarsMatchScope = !isGrouped || !currentRestaurantId

  const fetchLedger = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      if (useGroupRollup) {
        // Group roll-up via the service-role edge fn (member-only RLS
        // would hide child-unit rows from a pure group owner).
        if (!currentGroupId) {
          // No resolved group yet — show empty, don't fire a bad request.
          setTotalMinutes(0)
          setEventCount(0)
          setAsOf(null)
          return
        }
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
        const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
        const { data: sess } = await supabase.auth.getSession()
        const token = sess?.session?.access_token || anonKey

        const resp = await fetch(`${supabaseUrl}/functions/v1/get-time-saved-summary`, {
          method: 'POST',
          headers: {
            'content-type':  'application/json',
            'authorization': `Bearer ${token}`,
            'apikey':        anonKey,
          },
          body: JSON.stringify({ group_id: currentGroupId }),
        })
        if (!resp.ok) {
          const errText = await resp.text()
          throw new Error(`HTTP ${resp.status}: ${errText.slice(0, 200) || resp.statusText}`)
        }
        const payload = await resp.json()
        if (payload?.error) {
          throw new Error(payload.message || 'Could not load time-saved summary.')
        }
        setTotalMinutes(Number(payload?.total_minutes ?? 0))
        setEventCount(Number(payload?.event_count ?? 0))
        setAsOf(payload?.as_of ?? null)
        return
      }

      // Single-unit OR grouped-with-unit-selected: client RLS read. Scope
      // to the selected unit only when one is set (matches useHuddleData).
      let query = supabase
        .from('savings_events')
        .select('time_saved_minutes, occurred_at')
        .eq('source', TIME_SAVED_SOURCE)
        .not('time_saved_minutes', 'is', null)
      if (currentRestaurantId) {
        query = query.eq('restaurant_id', currentRestaurantId)
      }
      const { data, error: qErr } = await query
      if (qErr) throw qErr

      const rows = Array.isArray(data) ? data : []
      let minutes = 0
      let latest = null
      for (const r of rows) {
        const m = Number(r.time_saved_minutes)
        if (!Number.isFinite(m)) continue
        minutes += m
        const occ = r.occurred_at ?? null
        if (occ && (!latest || new Date(occ).getTime() > new Date(latest).getTime())) {
          latest = occ
        }
      }
      setTotalMinutes(minutes)
      setEventCount(rows.length)
      setAsOf(latest)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useTimeSaved]', e)
      setError('Could not load your time-saved summary.')
      setTotalMinutes(0)
      setEventCount(0)
      setAsOf(null)
    } finally {
      setLoading(false)
    }
  }, [useGroupRollup, currentRestaurantId, currentGroupId])

  useEffect(() => { fetchLedger() }, [fetchLedger])

  const totalHours = useMemo(() => totalMinutes / 60, [totalMinutes])

  return {
    totalMinutes,
    totalHours,
    asOf,
    eventCount,
    loading,
    error,
    // True when the secondary dollars figure is scope-compatible with the
    // hours headline (group roll-up or single-unit account). False when a
    // grouped account has a single unit selected — the card hides dollars.
    dollarsMatchScope,
    refresh: fetchLedger,
  }
}
